import * as Sentry from "@sentry/react";
import { logErrorTelemetry } from "@/hooks/useErrorTelemetry";

/**
 * Attach the current authenticated user to Sentry's scope so subsequent
 * events carry actor identity for triage. Called from `useAuth`'s
 * onAuthStateChange handler so the scope stays in sync with the session.
 *
 * Important: PII discipline. The scrub helper in this file already wipes
 * `event.user` down to just `{ id }` (see `scrubEvent`), so even if a
 * caller mistakenly passes email/name here, those fields are dropped
 * before the event leaves the browser. Belt-and-suspenders: callers
 * should ALSO not pass anything beyond the user id.
 */
export const setSentryUser = (userId: string | null): void => {
  if (userId) {
    Sentry.setUser({ id: userId });
  } else {
    // Pass null to drop the user from the scope on sign-out. Otherwise
    // a post-signout error would be attributed to the previous user,
    // which is misleading during multi-user-on-shared-device scenarios.
    Sentry.setUser(null);
  }
};

type Primitive = string | number | boolean | null | undefined;

type Serializable =
  | Primitive
  | Serializable[]
  | { [key: string]: Serializable };

const SENSITIVE_KEYS = [
  "password",
  "passcode",
  "secret",
  "token",
  "access_token",
  "refresh_token",
  "authorization",
  "cookie",
  "set-cookie",
  "api_key",
  "apikey",
  "session",
  "credit",
  "card",
  "ssn",
  "email",
  "phone",
  "address",
];

const REDACTED_VALUE = "[REDACTED]";

const isSensitiveKey = (key: string) => {
  const lowered = key.toLowerCase();
  return SENSITIVE_KEYS.some((sensitive) => lowered.includes(sensitive));
};

const scrubValue = (value: Serializable): Serializable => {
  if (Array.isArray(value)) {
    return value.map((entry) => scrubValue(entry));
  }

  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, nested]) => {
        if (isSensitiveKey(key)) {
          return [key, REDACTED_VALUE];
        }
        return [key, scrubValue(nested as Serializable)];
      })
    );
  }

  return value;
};

const scrubEvent = (event: Sentry.Event): Sentry.Event => {
  const scrubbed: Sentry.Event = {
    ...event,
    extra: event.extra ? (scrubValue(event.extra as Serializable) as Sentry.Event["extra"]) : event.extra,
    contexts: event.contexts
      ? (scrubValue(event.contexts as Serializable) as Sentry.Event["contexts"])
      : event.contexts,
    tags: event.tags ? (scrubValue(event.tags as Serializable) as Sentry.Event["tags"]) : event.tags,
  };

  if (event.user) {
    scrubbed.user = event.user.id ? { id: event.user.id } : undefined;
  }

  if (event.request) {
    const requestUrl = event.request.url ? new URL(event.request.url) : undefined;
    if (requestUrl) {
      requestUrl.search = "";
    }

    scrubbed.request = {
      url: requestUrl?.toString(),
      method: event.request.method,
    };
  }

  return scrubbed;
};

const captureBrowserError = (error: unknown, context?: Record<string, unknown>) => {
  Sentry.withScope((scope) => {
    if (context) {
      scope.setContext("global", scrubValue(context as Serializable) as Record<string, unknown>);
    }
    Sentry.captureException(error);
  });
};

/**
 * Log an error to the error_telemetry table via fire-and-forget.
 * Wrapped in try/catch so telemetry failures never break the app.
 */
const logGlobalErrorToTelemetry = (error: Error, source: string) => {
  try {
    logErrorTelemetry({
      errorMessage: error.message,
      errorName: error.name,
      route: typeof window !== "undefined" ? window.location.pathname : "unknown",
      errorStack: error.stack ?? null,
      metadata: { source },
    });
  } catch {
    // Fire-and-forget: never let telemetry logging break the app
  }
};

export const initMonitoring = () => {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  const sentryEnabled = Boolean(dsn);

  if (sentryEnabled) {
    Sentry.init({
      dsn,
      environment: import.meta.env.MODE,
      beforeSend: scrubEvent,
      tracesSampleRate: 0.1,
    });
  }

  // Always install global error handlers so errors reach error_telemetry
  // regardless of whether Sentry is configured.

  window.onerror = (message, _source, lineno, colno, error) => {
    const resolved = error ?? new Error(String(message));
    if (sentryEnabled) {
      captureBrowserError(resolved, { source: _source, lineno, colno });
    }
    logGlobalErrorToTelemetry(resolved, "window.onerror");
  };

  window.onunhandledrejection = (event) => {
    const resolved = event.reason instanceof Error
      ? event.reason
      : new Error(String(event.reason ?? "Unhandled promise rejection"));
    if (sentryEnabled) {
      captureBrowserError(resolved);
    }
    logGlobalErrorToTelemetry(resolved, "unhandledrejection");
  };

  window.addEventListener("error", (event) => {
    if (event.error) {
      if (sentryEnabled) {
        captureBrowserError(event.error);
      }
      logGlobalErrorToTelemetry(event.error, "error_event");
    }
  });
};
